import { faSearch } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import debounce from "lodash-es/debounce";
import { stringify } from "query-string";
import React from "react";

import SearchResults from "@/mk2/apps/blogs/components/SearchResults";
import { Props } from "@/mk2/apps/counsellings/containers/CounsellingsHome/CounsellingsHomePage";
import { counsellingsHomeUrl, counsellingsTopicUrl } from "@/mk2/apps/counsellings/urls";
import Feed from "@/mk2/apps/forum/components/Feed";
import { COUNSELLING_CATEGORY_ID } from "@/mk2/apps/forum/constants";
import { CounsellingEntity } from "@/mk2/apps/forum/schemas";
import { ClearableInput } from "@/mk2/components/ClearableInput";
import { Img } from "@/mk2/components/Img";
import { Interpolate } from "@/mk2/components/Interpolate";
import { ListItem } from "@/mk2/components/ListItem";
import { ListItemIcon } from "@/mk2/components/ListItemIcon";
import { url } from "@/mk2/helpers/urls";
import {
    COUNSELLING_ALL_TOPICS,
    COUNSELLING_ARCHIVED_TOPICS,
    COUNSELLING_NEWEST_RESPONSES,
    COUNSELLING_OTHER_TOPICS,
    COUNSELLING_OUR_EXPERTS_HELPED_num_MOTHERS,
    COUNSELLING_SEARCH_COUNSELLINGS_PLACEHOLDER,
    COUNSELLING_SEARCH_COUNSELLINGS_RESULT_query_count,
    COUNSELLING_TOPICS_I_AM_FOLLOWING,
    TOPIC_MODERATOR,
} from "@/mk/autogenerated/translation";
import { isAuthenticatedAs } from "@/mk/bazaar/common/userUtils";
import assetUrl from "@/mk/common/assetUrl";

import styles from "./CounsellingsHome.mscss";

interface State {
    query: string;
}

class CounsellingsHome extends React.PureComponent<Props, State> {
    private searchRedirectDebounced: () => void;

    constructor(props) {
        super(props);

        this.state = {
            query: props.query,
        };

        this.searchRedirectDebounced = debounce(this.searchRedirect, 500);
    }

    public render() {
        const {
            complaintsStatisticsPerTopic,
            counsellings,
            followingTopics,
            lastVisitCounts,
            loadingState,
            permissions,
            query,
            requestUser,
            resultsCount,
            topicStats,
            totalAnswers,
        } = this.props;

        const archivedCounsellings = counsellings.filter((c) => c.isArchived);
        const followingCounsellings = counsellings
            .filter((c) =>
                Boolean(
                    followingTopics
                        .filter((t) => t.category.id === COUNSELLING_CATEGORY_ID)
                        .find((t) => t.id === c.topic.id),
                ),
            )
            .filter((c) => !archivedCounsellings.includes(c));

        // Filter topics which are already in the previous lists(followingTopicsCounsellings or archivedCounsellings)
        const counsellingsWithoutFollowing = counsellings
            .filter((c) => !followingCounsellings.includes(c))
            .filter((c) => !archivedCounsellings.includes(c));

        return (
            <div>
                <div className={styles.CounsellingsHome__header}>
                    <Img
                        className={styles.CounsellingsHome__header__img}
                        width={102}
                        height={120}
                        src={`${assetUrl}img/miky/doc-miky@2x.png`}
                    />
                    {totalAnswers ? (
                        <Interpolate
                            i18nKey={COUNSELLING_OUR_EXPERTS_HELPED_num_MOTHERS}
                            num={<b>{totalAnswers}</b>}
                            parent="div"
                        />
                    ) : null}
                </div>

                <div className={styles.CounsellingsHome__search__container}>
                    <ClearableInput
                        icon={
                            <label htmlFor="search">
                                <FontAwesomeIcon icon={faSearch} />
                            </label>
                        }
                        hasValue={Boolean(this.state.query)}
                        onClear={this.onCancelFilters}
                    >
                        <input
                            id="search"
                            type="text"
                            value={this.state.query || ""}
                            onChange={this.onSearchChange}
                            placeholder={COUNSELLING_SEARCH_COUNSELLINGS_PLACEHOLDER}
                        />
                    </ClearableInput>
                </div>

                {query && (
                    <SearchResults
                        loadingState={loadingState}
                        hasData={Boolean(resultsCount)}
                        onCancelFilters={this.onCancelFilters}
                    >
                        <Interpolate
                            i18nKey={COUNSELLING_SEARCH_COUNSELLINGS_RESULT_query_count}
                            query={<strong>{query}</strong>}
                            count={resultsCount}
                        />
                    </SearchResults>
                )}

                {!query && followingCounsellings.length > 0 && (
                    <>
                        <div
                            className={cx(
                                styles.CounsellingsHome__topicsHeading,
                                styles["CounsellingsHome__topicsHeading--first"],
                            )}
                        >
                            <span className={styles.CounsellingsHome__topicsHeading__text}>
                                {COUNSELLING_TOPICS_I_AM_FOLLOWING}
                            </span>
                        </div>

                        {followingCounsellings.map((item, idx) => {
                            const counsellor = item?.counsellors?.find((c) => c.isActive);

                            return (
                                <ListItem
                                    key={item.id}
                                    icon={
                                        (isAuthenticatedAs(
                                            requestUser,
                                            item.topic.topicModerators?.map((tm) => tm.user) ||
                                                item.topic.moderators,
                                        ) ||
                                            permissions.indexOf("forum.can_manage_forum") >= 0) &&
                                        complaintsStatisticsPerTopic[item.topic.id] > 0 ? (
                                            <div className={styles.ForumCategories__listItemIcon}>
                                                <ListItemIcon
                                                    lazy
                                                    photo={
                                                        counsellor.avatarPhoto ??
                                                        item.counsellorAvatarPhoto
                                                    }
                                                    photoVersion="s150x150"
                                                />
                                                <span
                                                    className={
                                                        styles.ForumCategories__listItemIcon__bullet
                                                    }
                                                />
                                            </div>
                                        ) : (
                                            <ListItemIcon
                                                lazy
                                                photo={
                                                    counsellor.avatarPhoto ??
                                                    item.counsellorAvatarPhoto
                                                }
                                                photoVersion="s150x150"
                                            />
                                        )
                                    }
                                    isLast={idx === followingCounsellings.length - 1}
                                    to={url(counsellingsTopicUrl, { topicSlug: item.topic.slug })}
                                    count={
                                        lastVisitCounts[item.topic.id] || topicStats[item.topic.id]
                                    }
                                    asBadge={Boolean(lastVisitCounts[item.topic.id])}
                                >
                                    {item.topic.name}
                                    {isAuthenticatedAs(
                                        requestUser,
                                        item.topic.topicModerators?.map((tm) => tm.user) ||
                                            item.topic.moderators,
                                    ) && (
                                        <span
                                            className={
                                                styles.ForumCategories__moderatorLabelWrapper
                                            }
                                        >
                                            <span
                                                className={styles.ForumCategories__moderatorLabel}
                                            >
                                                {TOPIC_MODERATOR}
                                            </span>
                                        </span>
                                    )}
                                </ListItem>
                            );
                        })}
                    </>
                )}

                {!query && counsellingsWithoutFollowing.length > 0 && (
                    <>
                        <div
                            className={cx(
                                styles.CounsellingsHome__topicsHeading,
                                followingCounsellings.length === 0 &&
                                    styles["CounsellingsHome__topicsHeading--first"],
                            )}
                        >
                            <span className={styles.CounsellingsHome__topicsHeading__text}>
                                {followingCounsellings.length
                                    ? COUNSELLING_OTHER_TOPICS
                                    : COUNSELLING_ALL_TOPICS}
                            </span>
                        </div>

                        {counsellingsWithoutFollowing.map((item, idx) =>
                            this.listItem(item, idx, counsellingsWithoutFollowing),
                        )}
                    </>
                )}

                {!query && archivedCounsellings.length > 0 && (
                    <>
                        <div className={styles.CounsellingsHome__topicsHeading}>
                            <span className={styles.CounsellingsHome__topicsHeading__text}>
                                {COUNSELLING_ARCHIVED_TOPICS}
                            </span>
                        </div>

                        {archivedCounsellings.map((item, idx) =>
                            this.listItem(item, idx, archivedCounsellings),
                        )}
                    </>
                )}

                {!query && (
                    <div className={styles.CounsellingsHome__topicsHeading}>
                        <span className={styles.CounsellingsHome__topicsHeading__text}>
                            {COUNSELLING_NEWEST_RESPONSES}
                        </span>
                    </div>
                )}

                <div className={styles.CounsellingsHome__feed}>
                    <Feed
                        {...this.props}
                        showTopic
                        showCreated
                        readonlyPosts={false}
                        inFeed
                        onLoad={this.onLoad}
                    />
                </div>
            </div>
        );
    }

    private listItem = (item: CounsellingEntity, idx, counsellingsInQuestion) => {
        const { requestUser, permissions, complaintsStatisticsPerTopic, topicStats } = this.props;

        let counsellor = item?.counsellors?.find((c) => c.isActive);

        // if no active counsellor (e.g. counselling is archived) show last counsellor
        if (!counsellor) {
            counsellor = item?.counsellors?.length
                ? item?.counsellors?.[item.counsellors.length - 1]
                : null;
        }

        return (
            <ListItem
                key={item.id}
                className={styles.CounsellingsHome__listItemWithVisitedColor}
                icon={
                    (isAuthenticatedAs(
                        requestUser,
                        item.topic.topicModerators?.map((tm) => tm.user) || item.topic.moderators,
                    ) ||
                        permissions.indexOf("forum.can_manage_forum") >= 0) &&
                    complaintsStatisticsPerTopic[item.topic.id] > 0 ? (
                        <div className={styles.ForumCategories__listItemIcon}>
                            <ListItemIcon
                                lazy
                                photo={counsellor?.avatarPhoto ?? item.counsellorAvatarPhoto}
                                photoVersion="s150x150"
                            />
                            <span className={styles.ForumCategories__listItemIcon__bullet} />
                        </div>
                    ) : (
                        <ListItemIcon
                            lazy
                            photo={counsellor?.avatarPhoto ?? item.counsellorAvatarPhoto}
                            photoVersion="s150x150"
                        />
                    )
                }
                isLast={idx === counsellingsInQuestion.length - 1}
                to={url(counsellingsTopicUrl, { topicSlug: item.topic.slug })}
                count={topicStats[item.topic.id]}
            >
                {item.topic.name}
                {isAuthenticatedAs(
                    requestUser,
                    item.topic.topicModerators?.map((tm) => tm.user) || item.topic.moderators,
                ) && (
                    <span className={styles.ForumCategories__moderatorLabelWrapper}>
                        <span className={styles.ForumCategories__moderatorLabel}>
                            {TOPIC_MODERATOR}
                        </span>
                    </span>
                )}
            </ListItem>
        );
    };

    private onLoad = (after?: number) => {
        const { onLoad, query } = this.props;
        onLoad(query, after);
    };

    private commonRedirect = (query) => {
        const {
            match: { params },
        } = this.props;

        const search = {
            ...(query !== null ? { q: query } : {}),
        };

        this.props.history.replace({
            pathname: url(counsellingsHomeUrl, params),
            search: search ? stringify(search) : undefined,
        });
    };

    private onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ query: event.currentTarget.value }, this.searchRedirectDebounced);
    };

    private searchRedirect = () => {
        this.commonRedirect(this.state.query);
    };

    private onCancelFilters = () => {
        this.setState({ query: null });
        this.commonRedirect(null);
    };
}

export default CounsellingsHome;
